// src/services/assetService.ts

import {
  Asset,
  AssetCategory,
  AssetCategoryHierarchyNode,
  AssetCreateInput,
  AssetMaster,
  AssetMasterCreateInput,
  AssetMasterUpdateInput,
  AssetUpdateInput,
  Company,
  CompanyHierarchyNode,
  CompanyHistoryData,
  Country,
  Department,
  Location,
  LocationHierarchyNode,
  LocationInput,
  Manufacturer,
  ManufacturerData,
  Model,
  ModelData,
  ReferenceData,
  Site,
  SiteInput,
  State,
  Supplier,
  SupplierInput,
  SupplierPaginationResponse,
  SupplierHistoryResponse,
  SiteHistoryResponse,
  Users,
  LocationHistoryResponse,
  AssetConditionHistoryResponse,
  AssetStatusHistoryResponse,
  ManufacturerHistoryResponse,
  ModelHistoryResponse,
  AssetCategoryHistoryResponse,
  DepartmentHistoryResponse,
} from '../types/asset.types';
import api, { apiFormData } from './api';
import { ApiEndpoints } from '../services/ApiUrls';
import { CompanyCreateInput } from '../store/api/companyApi';
import { getErrorMessage } from '../utils/errorUtils';

interface GetAssetsParams {
  search?: string;
  categoryId?: string[];
  locationId?: string[];
  departmentId?: string[];
  page?: number;
  limit?: number;
  dropdown?: boolean;
  // Overrides the base URL (defaults to ApiEndpoints.assets.list) - e.g.
  // ApiEndpoints.assets.transferSelection for the transfer creation picker.
  endpoint?: string;
}

interface AssetCategoryListResponse {
  results: AssetCategory[];
  count: number;
}

interface DepartmentListResponse {
  results: Department[];
  count: number;
}

const assetService = {
  // Asset methods
  getAssets: async (params?: GetAssetsParams): Promise<Asset[]> => {
    const queryParams = new URLSearchParams();

    if (params?.search) {
      queryParams.append('search', params.search);
    }

    if (params?.categoryId && params.categoryId.length > 0) {
      queryParams.append('asset_categories', params.categoryId.join(','));
    }

    if (params?.locationId && params.locationId.length > 0) {
      queryParams.append('locations', params.locationId.join(','));
    }

    if (params?.departmentId && params.departmentId.length > 0) {
      queryParams.append('departments', params.departmentId.join(','));
    }

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }

    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    if (params?.dropdown) {
      queryParams.append('dropdown', params.dropdown.toString());
    }
    // Make API call with query parameters
    const base = params?.endpoint || ApiEndpoints.assets.list;
    const url = params ? `${base}?${queryParams.toString()}` : base;
    const response = await api.get(url);
    return response.data;
  },

  // New dropdown-specific method for assets
  getAssetsDropdown: async (params?: GetAssetsParams): Promise<Asset[]> => {
    const queryParams = new URLSearchParams();

    if (params?.search) {
      queryParams.append('search', params.search);
    }

    if (params?.categoryId && params.categoryId.length > 0) {
      queryParams.append('asset_categories', params.categoryId.join(','));
    }

    if (params?.locationId && params.locationId.length > 0) {
      queryParams.append('locations', params.locationId.join(','));
    }

    if (params?.departmentId && params.departmentId.length > 0) {
      queryParams.append('departments', params.departmentId.join(','));
    }

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }

    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    // Always set dropdown to true for this method
    queryParams.append('dropdown', 'true');

    // Use the new dropdown endpoint
    const url = `assets/dropdown/?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  getAssetById: async (id: string): Promise<Asset> => {
    const response = await api.get(ApiEndpoints.assets.detail(id));
    return response.data;
  },

  createAsset: async (asset: AssetCreateInput): Promise<Asset> => {
    if (asset.documents && asset.documents.length > 0) {
      const formData = new FormData();
      const assetData = { ...asset };
      delete assetData.documents;
      formData.append('data', JSON.stringify(assetData));
      asset.documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });
      const response = await apiFormData.post(
        ApiEndpoints.assets.list,
        formData
      );
      return response.data.asset;
    }
    const response = await api.post(ApiEndpoints.assets.list, asset);
    return response.data.asset;
  },

  updateAsset: async (id: string, asset: AssetUpdateInput): Promise<Asset> => {
    const response = await api.put(ApiEndpoints.assets.detail(id), asset);
    return response.data;
  },

  deleteAsset: async (id: string): Promise<void> => {
    await api.delete(ApiEndpoints.assets.detail(id));
  },

  getAssetsByCategory: async (category: string): Promise<Asset[]> => {
    const response = await api.get(ApiEndpoints.assets.byCategory(category));
    return response.data;
  },

  getAssetsByLocation: async (location: string): Promise<Asset[]> => {
    const response = await api.get(ApiEndpoints.assets.byLocation(location));
    return response.data;
  },

  getAssetsByStatus: async (status: string): Promise<Asset[]> => {
    const response = await api.get(ApiEndpoints.assets.byStatus(status));
    return response.data;
  },

  assignAsset: async (assetId: string, userId: string): Promise<Asset> => {
    const response = await api.post(ApiEndpoints.assets.assign(assetId), {
      userId,
    });
    return response.data;
  },

  unassignAsset: async (assetId: string): Promise<Asset> => {
    const response = await api.post(ApiEndpoints.assets.unassign(assetId));
    return response.data;
  },

  getAssetTransferHistoryById: async (id: string): Promise<AssetMaster> => {
    const response = await api.get(ApiEndpoints.assets.transferHistory(id));
    return response.data;
  },

  getAssetAuditRequestsById: async (id: string): Promise<AssetMaster> => {
    const response = await api.get(ApiEndpoints.assets.auditRequests(id));
    return response.data;
  },

  getAssetScheduledActivitiesById: async (id: string): Promise<AssetMaster> => {
    const response = await api.get(ApiEndpoints.assets.scheduledActivities(id));
    return response.data;
  },

  // Asset Master methods
  getAssetMasters: async (): Promise<AssetMaster[]> => {
    const response = await api.get(ApiEndpoints.assetMasters.list);
    return response.data;
  },

  getAssetMasterById: async (id: string): Promise<AssetMaster> => {
    const response = await api.get(ApiEndpoints.assetMasters.detail(id));
    return response.data;
  },

  createAssetMaster: async (
    assetMaster: AssetMasterCreateInput
  ): Promise<AssetMaster> => {
    const response = await api.post(
      ApiEndpoints.assetMasters.list,
      assetMaster
    );
    return response.data.asset;
  },

  updateAssetMaster: async (
    id: string,
    assetMaster: AssetMasterUpdateInput
  ): Promise<AssetMaster> => {
    const response = await api.put(
      ApiEndpoints.assetMasters.detail(id),
      assetMaster
    );
    return response.data;
  },

  deleteAssetMaster: async (id: string): Promise<void> => {
    await api.delete(ApiEndpoints.assetMasters.detail(id));
  },

  getReferenceData: async (): Promise<ReferenceData> => {
    const response = await api.get(ApiEndpoints.assetMasters.referenceData);
    return response.data;
  },

  // Manufacturer methods
  getManufacturers: async (
    search = '',
    dropdown?: boolean,
    ordering?: string,
    searchParams?: Record<string, string | boolean>
  ): Promise<Manufacturer[]> => {
    const params: Record<string, string | boolean> = {};
    if (search) params.search = search;
    if (dropdown !== undefined) params.dropdown = dropdown;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }

    const response = await api.get(ApiEndpoints.manufacturers.list, { params });
    return response.data.results || [];
  },

  // New dropdown-specific method for manufacturers
  getManufacturersDropdown: async (
    search = '',
    ordering?: string,
    searchParams?: Record<string, string | boolean>,
    page?: number,
    limit?: number
  ): Promise<Manufacturer[]> => {
    const params: Record<string, string | boolean> = {};
    if (search) params.search = search;
    if (ordering !== undefined) params.ordering = ordering;
    if (page !== undefined) params.page = page;
    if (limit !== undefined) params.limit = limit;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }

    const response = await api.get('assets/manufacturers/dropdown/', {
      params,
    });
    return response.data.results || [];
  },

  getManufacturersPaginated: async (
    params: Record<string, string | boolean | number | undefined> = {}
  ): Promise<ManufacturerData> => {
    const response = await api.get(ApiEndpoints.manufacturers.list, { params });
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data, count: data.length, next: '', previous: '' };
    }
    if (Array.isArray(data?.results)) {
      return {
        results: data.results,
        count:
          typeof data.count === 'number' ? data.count : data.results.length,
        next: data.next ?? '',
        previous: data.previous ?? '',
      };
    }
    return { results: [], count: 0, next: '', previous: '' };
  },

  getManufacturerById: async (id: string): Promise<Manufacturer> => {
    const response = await api.get(ApiEndpoints.manufacturers.detail(id));
    return response.data;
  },

  createManufacturer: async (
    manufacturer: Partial<Manufacturer>
  ): Promise<Manufacturer> => {
    const response = await api.post(
      ApiEndpoints.manufacturers.list,
      manufacturer
    );
    return response.data.manufacturer || response.data;
  },

  updateManufacturer: async (
    id: string,
    manufacturer: Partial<Manufacturer>,
    changeReason?: string
  ): Promise<Manufacturer> => {
    const response = await api.patch(ApiEndpoints.manufacturers.detail(id), {
      ...manufacturer,
      change_reason: changeReason,
    });
    return response.data.manufacturer || response.data;
  },

  deleteManufacturer: async (id: string): Promise<null> => {
    const response = await api.delete(ApiEndpoints.manufacturers.detail(id));
    return response.data;
  },

  // Model methods
  getModels: async (
    search = '',
    manufacturerId?: string,
    ordering?: string,
    searchParams?: Record<string, string | boolean>
  ): Promise<Model[]> => {
    const params: Record<string, string | boolean> = {};
    if (search) params.search = search;
    if (manufacturerId) params.manufacturer = manufacturerId;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }

    const response = await api.get(ApiEndpoints.models.list, { params });
    return response.data.results || [];
  },

  // New dropdown-specific method for models
  getModelsDropdown: async (
    search = '',
    manufacturerId?: string,
    ordering?: string,
    searchParams?: Record<string, string | boolean>,
    page?: number,
    limit?: number
  ): Promise<Model[]> => {
    const params: Record<string, string | number | boolean> = {};
    if (search) params.search = search;
    if (manufacturerId) params.manufacturer = manufacturerId;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    if (page !== undefined) params.page = page;
    if (limit !== undefined) params.limit = limit;

    const response = await api.get('master/models/dropdown/', { params });
    return response.data.results || [];
  },

  getModelsPaginated: async (
    params: Record<string, string | boolean | number | undefined> = {}
  ): Promise<ModelData> => {
    const response = await api.get(ApiEndpoints.models.list, { params });
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data, count: data.length, next: '', previous: '' };
    }
    if (Array.isArray(data?.results)) {
      return {
        results: data.results,
        count:
          typeof data.count === 'number' ? data.count : data.results.length,
        next: data.next ?? '',
        previous: data.previous ?? '',
      };
    }
    return { results: [], count: 0, next: '', previous: '' };
  },

  getModelById: async (id: string): Promise<Model> => {
    const response = await api.get(ApiEndpoints.models.detail(id));
    return response.data;
  },

  createModel: async (model: Partial<Model>): Promise<Model> => {
    const response = await api.post(ApiEndpoints.models.list, model);
    return response.data;
  },

  updateModel: async (
    id: string,
    model: Partial<Model>,
    changeReason: string
  ): Promise<Model> => {
    const response = await api.patch(ApiEndpoints.models.detail(id), {
      ...model,
      change_reason: changeReason,
    });
    return response.data;
  },

  deleteModel: async (id: string): Promise<null> => {
    const response = await api.delete(ApiEndpoints.models.detail(id));
    return response.data;
  },

  // Company methods (using RTK Query)
  getCompanies: async (
    search = '',
    parentCompanyId?: string | null,
    isActive?: boolean
  ): Promise<Company[]> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const filter = { search, parentCompanyId, isActive };
    const result = await store.dispatch(
      companyApi.endpoints.getCompanies.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch companies');
    return result.data?.results || [];
  },

  getCompanyById: async (id: string): Promise<Company> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const result = await store.dispatch(
      companyApi.endpoints.getCompanyById.initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch company with ID: ${id}`);
    return result.data;
  },

  createCompany: async (company: Partial<Company>): Promise<Company> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const payload: CompanyCreateInput = {
      name: company.name ?? '',
      description: company.description ?? '',
      parentCompany: company.parentCompany ?? '',
      isActive: company.is_active ?? true,
    };

    const result = await store.dispatch(
      companyApi.endpoints.createCompany.initiate(payload)
    );

    if ('error' in result || !result.data)
      throw new Error('Failed to create company');
    return result.data;
  },

  updateCompany: async (
    id: string,
    company: Partial<Company>
  ): Promise<Company> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const result = await store.dispatch(
      companyApi.endpoints.updateCompany.initiate({ id, company })
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to update company with ID: ${id}`);
    return result.data;
  },

  getCompanyHierarchy: async (
    rootCompanyId?: string,
    includeInactive = false
  ): Promise<CompanyHierarchyNode[]> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const filter = { rootCompanyId, includeInactive };
    const result = await store.dispatch(
      companyApi.endpoints.getCompanyHierarchy.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch company hierarchy');
    return result.data ?? [];
  },

  getCompanyHistory: async (id: string): Promise<CompanyHistoryData> => {
    const { store } = await import('../store');
    const { companyApi } = await import('../store/api/companyApi');

    const result = await store.dispatch(
      companyApi.endpoints.getCompanyHistory.initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch history for company with ID: ${id}`);
    return result.data;
  },

  // Department methods
  getDepartments: async (
    search = '',
    dropdown?: boolean,
    ordering?: string,
    searchParams?: Record<string, string | boolean>
  ): Promise<Department[]> => {
    const params: Record<string, string | boolean | null> = {};
    if (search) params.search = search;
    if (dropdown !== undefined) params.dropdown = dropdown;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }

    const response = await api.get(ApiEndpoints.departments.list, { params });
    return response.data.results;
  },

  // New dropdown-specific method for departments
  getDepartmentsDropdown: async (
    search = '',
    ordering?: string,
    searchParams?: Record<string, string | boolean>,
    page?: number,
    limit?: number
  ): Promise<Department[]> => {
    const params: Record<string, string | number | boolean | null> = {};
    if (search) params.search = search;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    if (page !== undefined) params.page = page;
    if (limit !== undefined) params.limit = limit;

    const response = await api.get('company/departments/dropdown/', { params });
    return response.data.results;
  },

  getDepartmentsPaginated: async (
    params: Record<string, string | boolean | number | null> = {},
    ordering?: string
  ): Promise<DepartmentListResponse> => {
    const queryParams: Record<string, string | boolean | number | null> = {
      ...params,
    };
    if (ordering !== undefined) queryParams.ordering = ordering;
    const response = await api.get(ApiEndpoints.departments.list, {
      params: queryParams,
    });
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data, count: data.length };
    }
    if (Array.isArray(data?.results)) {
      return {
        results: data.results,
        count:
          typeof data.count === 'number' ? data.count : data.results.length,
      };
    }
    return { results: [], count: 0 };
  },

  getDepartmentById: async (id: string): Promise<Department> => {
    const response = await api.get(ApiEndpoints.departments.detail(id));
    return response.data;
  },

  createDepartment: async (
    department: Partial<Department>
  ): Promise<Department> => {
    const response = await api.post(ApiEndpoints.departments.list, department);
    return response.data;
  },

  updateDepartment: async (
    id: string,
    department: Partial<Department>
  ): Promise<Department> => {
    const response = await api.patch(
      ApiEndpoints.departments.detail(id),
      department
    );
    return response.data;
  },

  deleteDepartment: async (id: string): Promise<void> => {
    await api.delete(ApiEndpoints.departments.detail(id));
  },

  // Asset Category methods
  getAssetCategories: async (
    search = '',
    parentCategoryId?: string | null,
    dropdown?: boolean,
    ordering?: string,
    searchParams?: Record<string, string | boolean>
  ): Promise<AssetCategory[]> => {
    const params: Record<string, string | boolean | null> = {};
    if (search) params.search = search;
    if (parentCategoryId !== undefined)
      params.parentCategoryId = parentCategoryId;
    if (dropdown !== undefined) params.dropdown = dropdown;
    if (ordering !== undefined) params.ordering = ordering;
    if (searchParams && Object.keys(searchParams).length > 0) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    const response = await api.get(ApiEndpoints.assetCategories.list, {
      params,
    });
    return response.data.results;
  },

  // New dropdown-specific method for asset categories
  getAssetCategoriesDropdown: async (
    search = '',
  ): Promise<AssetCategory[]> => {
    const params: Record<string, string | boolean | number | null> = {};
    params.paginate = false; // Disable pagination for dropdown
    if (search) params.search = search;
    const response = await api.get('assets/asset-categories/dropdown/', {
      params,
    });
    return response.data.results;
  },

  getAssetCategoriesPaginated: async (
    params: Record<string, string | boolean | null> = {},
    ordering?: string
  ): Promise<AssetCategoryListResponse> => {
    const queryParams: Record<string, string | boolean | null> = { ...params };
    if (ordering !== undefined) queryParams.ordering = ordering;
    const response = await api.get(ApiEndpoints.assetCategories.list, {
      params: queryParams,
    });
    const data = response.data;
    if (Array.isArray(data)) {
      return { results: data, count: data.length };
    }
    if (Array.isArray(data?.results)) {
      return {
        results: data.results,
        count:
          typeof data.count === 'number' ? data.count : data.results.length,
      };
    }
    return { results: [], count: 0 };
  },

  getAssetCategoryById: async (id: string): Promise<AssetCategory> => {
    const response = await api.get(ApiEndpoints.assetCategories.detail(id));
    return response.data;
  },

  createAssetCategory: async (
    category: Partial<AssetCategory>
  ): Promise<AssetCategory> => {
    const response = await api.post(
      ApiEndpoints.assetCategories.list,
      category
    );
    return response.data;
  },

  updateAssetCategory: async (
    id: string,
    category: Partial<AssetCategory>,
    changeReason: string
  ): Promise<AssetCategory> => {
    const response = await api.patch(ApiEndpoints.assetCategories.detail(id), {
      ...category,
      change_reason: changeReason,
    });
    return response.data;
  },

  deleteAssetCategory: async (id: string): Promise<void> => {
    await api.delete(ApiEndpoints.assetCategories.detail(id));
  },

  getAssetCategoryHierarchy: async (
    companyId: string,
    rootCategoryId?: string,
    includeInactive = false
  ): Promise<AssetCategoryHierarchyNode[]> => {
    const params: Record<string, string | boolean> = {
      companyId,
      includeInactive,
    };
    if (rootCategoryId) params.rootCategoryId = rootCategoryId;

    try {
      const response = await api.get(ApiEndpoints.assetCategories.hierarchy, {
        params,
      });

      if (!response.data) {
        console.error('No data in response:', response);
        return [];
      }

      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (
        response.data &&
        typeof response.data === 'object' &&
        'results' in response.data &&
        Array.isArray(response.data.results)
      ) {
        return response.data.results;
      }

      console.error('Unexpected response format:', response.data);
      return [];
    } catch (error) {
      console.error('Error fetching asset category hierarchy:', error);
      throw error;
    }
  },

  // Country methods (using RTK Query)
  getCountries: async (
    activeOnly = false,
    search?: string
  ): Promise<Country[]> => {
    const { store } = await import('../store');
    const { countryApi } = await import('../store/api/countryApi');

    const filter: { isActive?: boolean; search?: string } = {
      isActive: activeOnly,
    };
    if (search) filter.search = search;

    const result = await store.dispatch(
      countryApi.endpoints.getCountries.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch countries');

    const data = result.data;
    const isPaginatedResponse = (
      obj: unknown
    ): obj is { results: Record<string, unknown>[] } => {
      if (!obj || typeof obj !== 'object') return false;
      const potentialPaginated = obj as { results?: unknown };
      return Array.isArray(potentialPaginated.results);
    };

    interface RawCountryData extends Omit<Country, 'isActive'> {
      is_active: boolean;
      [key: string]: unknown;
    }

    const transformCountry = (country: Record<string, unknown>): Country => {
      const rawCountry = country as RawCountryData;
      if (rawCountry.is_active !== undefined && !('isActive' in rawCountry)) {
        return {
          ...rawCountry,
          isActive: rawCountry.is_active,
        } as Country;
      }
      return country as Country;
    };

    const countries = isPaginatedResponse(data)
      ? data.results.map(transformCountry)
      : ((data as Record<string, unknown>[]) || []).map(transformCountry);

    return countries;
  },

  getCountryById: async (id: string): Promise<Country> => {
    const { store } = await import('../store');
    const { countryApi } = await import('../store/api/countryApi');

    const result = await store.dispatch(
      countryApi.endpoints.getCountryById.initiate(id)
    );

    if ('error' in result || !result.data) {
      throw new Error(`Failed to fetch country with ID: ${id}`);
    }

    interface CountryWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const transformedData: CountryWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as Country;
  },

  createCountry: async (country: Partial<Country>): Promise<Country> => {
    const { store } = await import('../store');
    const { countryApi } = await import('../store/api/countryApi');

    interface CountryWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const mappedCountry: CountryWithIsActive = { ...country };
    if ('isActive' in mappedCountry) {
      mappedCountry.is_active = mappedCountry.isActive;
    }

    const result = await store.dispatch(
      countryApi.endpoints[
        ApiEndpoints.rtkQuery.countries.createCountry
      ].initiate(mappedCountry)
    );

    if ('error' in result || !result.data)
      throw new Error('Failed to create country');

    const transformedData: CountryWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as Country;
  },

  updateCountry: async (
    id: string,
    country: Partial<Country>
  ): Promise<Country> => {
    const { store } = await import('../store');
    const { countryApi } = await import('../store/api/countryApi');

    interface CountryWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const mappedCountry: CountryWithIsActive = { ...country };
    if ('isActive' in mappedCountry) {
      mappedCountry.is_active = mappedCountry.isActive;
    }

    const result = await store.dispatch(
      countryApi.endpoints.updateCountry.initiate({
        id,
        country: mappedCountry,
      })
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to update country with ID: ${id}`);

    const transformedData: CountryWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as Country;
  },

  getCountryHistory: async (id: string): Promise<CountryHistoryData> => {
    const { store } = await import('../store');
    const { countryApi } = await import('../store/api/countryApi');

    const result = await store.dispatch(
      countryApi.endpoints[
        ApiEndpoints.rtkQuery.countries.getCountryHistory(id)
      ].initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch history for country with ID: ${id}`);

    const historyData = { ...result.data };

    if (historyData.changes && Array.isArray(historyData.changes)) {
      historyData.changes = historyData.changes.map((change) => {
        const processedChange = { ...change };
        if (processedChange.fieldName === 'is_active') {
          processedChange.fieldName = 'isActive';
        }
        return processedChange;
      });
    }

    return historyData;
  },

  // State methods (using RTK Query)
  getStates: async (
    countryId?: string,
    activeOnly = false
  ): Promise<State[]> => {
    const { store } = await import('../store');
    const { stateApi } = await import('../store/api/stateApi');

    const filter = { countryId, isActive: activeOnly };

    const result = await store.dispatch(
      stateApi.endpoints.getStates.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch states');

    const data = result.data;
    const isPaginatedResponse = (
      obj: unknown
    ): obj is { results: Record<string, unknown>[] } => {
      if (!obj || typeof obj !== 'object') return false;
      const potentialPaginated = obj as { results?: unknown };
      return Array.isArray(potentialPaginated.results);
    };

    interface RawStateData extends Omit<State, 'isActive'> {
      is_active: boolean;
      [key: string]: unknown;
    }

    const transformState = (state: Record<string, unknown>): State => {
      const rawState = state as RawStateData;
      if (rawState.is_active !== undefined && !('isActive' in rawState)) {
        return {
          ...rawState,
          isActive: rawState.is_active,
        } as State;
      }
      return state as State;
    };

    const states = isPaginatedResponse(data)
      ? data.results.map(transformState)
      : ((data as Record<string, unknown>[]) || []).map(transformState);

    return states;
  },

  getStateById: async (id: string): Promise<State> => {
    const { store } = await import('../store');
    const { stateApi } = await import('../store/api/stateApi');

    const result = await store.dispatch(
      stateApi.endpoints.getStateById.initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch state with ID: ${id}`);

    interface StateWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const transformedData: StateWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as State;
  },

  createState: async (state: Partial<State>): Promise<State> => {
    const { store } = await import('../store');
    const { stateApi } = await import('../store/api/stateApi');

    interface StateWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const mappedState: StateWithIsActive = { ...state };
    if ('isActive' in mappedState) {
      mappedState.is_active = mappedState.isActive;
    }

    const result = await store.dispatch(
      stateApi.endpoints[ApiEndpoints.rtkQuery.states.createState].initiate(
        mappedState
      )
    );

    if ('error' in result || !result.data)
      throw new Error('Failed to create state');

    const transformedData: StateWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as State;
  },

  updateState: async (id: string, state: Partial<State>): Promise<State> => {
    const { store } = await import('../store');
    const { stateApi } = await import('../store/api/stateApi');

    interface StateWithIsActive extends Record<string, unknown> {
      isActive?: boolean;
      is_active?: boolean;
    }

    const mappedState: StateWithIsActive = { ...state };
    if ('isActive' in mappedState) {
      mappedState.is_active = mappedState.isActive;
    }

    const result = await store.dispatch(
      stateApi.endpoints.updateState.initiate({
        id,
        state: mappedState,
      })
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to update state with ID: ${id}`);

    const transformedData: StateWithIsActive = { ...result.data };
    if ('is_active' in transformedData && !('isActive' in transformedData)) {
      transformedData.isActive = transformedData.is_active;
    }

    return transformedData as State;
  },

  getStateHistory: async (id: string): Promise<StateHistoryData> => {
    const { store } = await import('../store');
    const { stateApi } = await import('../store/api/stateApi');

    const result = await store.dispatch(
      stateApi.endpoints[
        ApiEndpoints.rtkQuery.states.getStateHistory(id)
      ].initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch history for state with ID: ${id}`);

    const historyData = { ...result.data };

    if (historyData.changes && Array.isArray(historyData.changes)) {
      historyData.changes = historyData.changes.map((change) => {
        const processedChange = { ...change };
        if (processedChange.fieldName === 'is_active') {
          processedChange.fieldName = 'isActive';
        }
        return processedChange;
      });
    }

    return historyData;
  },

  // Site methods (using RTK Query)
  getSites: async (
    search?: string,
    companyId?: string,
    countryId?: string,
    stateId?: string,
    page = 1,
    limit = 10,
    ordering?: string
  ): Promise<Site[]> => {
    const { store } = await import('../store');
    const { siteApi } = await import('../store/api/siteApi');

    const filter = {
      search,
      companyId,
      countryId,
      stateId,
      page,
      limit,
      ordering,
    };

    const result = await store.dispatch(
      siteApi.endpoints.getSites.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch sites');
    return result.data || [];
  },

  // New dropdown-specific method for sites
  getSitesDropdown: async (
    search?: string,
    companyId?: string,
    countryId?: string,
    stateId?: string,
    page = 1,
    limit = 10,
    ordering?: string
  ): Promise<Site[]> => {
    const { store } = await import('../store');
    const { siteApi } = await import('../store/api/siteApi');

    const filter = {
      search,
      companyId,
      countryId,
      stateId,
      page,
      limit,
      ordering,
    };

    const result = await store.dispatch(
      siteApi.endpoints.getSitesDropdown.initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch sites dropdown');
    return result.data || [];
  },

  getSiteById: async (id: string): Promise<Site> => {
    const { store } = await import('../store');
    const { siteApi } = await import('../store/api/siteApi');

    const result = await store.dispatch(
      siteApi.endpoints[ApiEndpoints.rtkQuery.sites.getSiteById(id)].initiate(
        id
      )
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch site with ID: ${id}`);
    return result.data;
  },

  createSite: async (site: SiteInput): Promise<Site> => {
    const { store } = await import('../store');
    const { siteApi } = await import('../store/api/siteApi');

    const result = await store.dispatch(
      siteApi.endpoints[ApiEndpoints.rtkQuery.sites.createSite].initiate(site)
    );

    if ('error' in result || !result.data)
      throw new Error('Failed to create site');
    return result.data;
  },

  updateSite: async (id: string, site: Partial<SiteInput>): Promise<Site> => {
    const { store } = await import('../store');
    const { siteApi } = await import('../store/api/siteApi');

    const result = await store.dispatch(
      siteApi.endpoints[ApiEndpoints.rtkQuery.sites.updateSite(id)].initiate({
        id,
        site,
      })
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to update site with ID: ${id}`);
    return result.data;
  },

  // Location methods (using RTK Query)
  getLocations: async (
    siteId: string,
    search?: string,
    locationType?: string,
    parentLocationId?: string,
    page = 1,
    pageSize = 10,
    sortField = 'name',
    sortDirection = 'asc'
  ): Promise<Location[]> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const filter = {
      siteId,
      search,
      locationType,
      parentLocationId,
      page,
      pageSize,
      sortField,
      sortDirection,
    };

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getLocations
      ].initiate(filter)
    );

    if ('error' in result) throw new Error('Failed to fetch locations');
    return result.data || [];
  },

  getAllLocations: async (search = ''): Promise<Location[]> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getAllLocations
      ].initiate(search)
    );

    if ('error' in result) throw new Error('Failed to fetch all locations');
    return result.data || [];
  },

  getLocationById: async (id: string): Promise<Location> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getLocationById(id)
      ].initiate(id)
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to fetch location with ID: ${id}`);
    return result.data;
  },

  createLocation: async (location: LocationInput): Promise<Location> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.createLocation
      ].initiate(location)
    );

    if ('error' in result || !result.data)
      throw new Error('Failed to create location');
    return result.data;
  },

  updateLocation: async (
    id: string,
    location: Partial<LocationInput>
  ): Promise<Location> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.updateLocation(id)
      ].initiate({ id, location })
    );

    if ('error' in result || !result.data)
      throw new Error(`Failed to update location with ID: ${id}`);
    return result.data;
  },

  getLocationHierarchy: async (
    siteId: string,
    rootLocationId?: string,
    includeInactive = false
  ): Promise<LocationHierarchyNode[]> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const filter = { siteId, rootLocationId, includeInactive };
    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getLocationHierarchy
      ].initiate(filter)
    );

    if ('error' in result)
      throw new Error('Failed to fetch location hierarchy');
    return result.data || [];
  },

  getLocationAssets: async (locationId: string): Promise<Asset[]> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getLocationAssets(locationId)
      ].initiate(locationId)
    );

    if ('error' in result)
      throw new Error(
        `Failed to fetch assets for location with ID: ${locationId}`
      );
    return result.data || [];
  },

  getLocationsByHierarchy: async (
    hierarchyPath: string
  ): Promise<Location[]> => {
    const { store } = await import('../store');
    const { locationApi } = await import('../store/api/locationApi');

    const filter = { hierarchyPath };
    const result = await store.dispatch(
      locationApi.endpoints[
        ApiEndpoints.rtkQuery.locations.getLocationsByHierarchy
      ].initiate(filter)
    );

    if ('error' in result)
      throw new Error('Failed to fetch locations by hierarchy');
    return result.data || [];
  },

  // Supplier methods
  getSuppliers: async (
    params: Record<string, string | boolean | number | undefined> = {}
  ): Promise<SupplierPaginationResponse> => {
    const response = await api.get(ApiEndpoints.suppliers.list, { params });
    return response.data;
  },

  // New dropdown-specific method for suppliers
  getSuppliersDropdown: async (
    params: {
      search?: string;
      categoryId?: string;
      locationId?: string;
      transactionTypeId?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      ordering?: string;
    } = {}
  ): Promise<SupplierPaginationResponse> => {
    const response = await api.get('suppliers/suppliers/dropdown/', { params });
    return response.data;
  },

  getSupplierById: async (id: string): Promise<Supplier> => {
    const response = await api.get(ApiEndpoints.suppliers.detail(id));
    return response.data;
  },

  createSupplier: async (supplier: SupplierInput): Promise<Supplier> => {
    const response = await api.post(ApiEndpoints.suppliers.list, supplier);
    return response.data;
  },

  deleteSupplier: async (id: string): Promise<Supplier> => {
    const response = await api.delete(ApiEndpoints.suppliers.detail(id));
    if (response.status !== 204) {
      throw new Error(`Failed to delete supplier with ID: ${id}`);
    }
  },

  updateSupplier: async (
    id: string,
    supplier: SupplierInput,
    changeReason?: string
  ): Promise<Supplier> => {
    const response = await api.patch(ApiEndpoints.suppliers.detail(id), {
      ...supplier,
      change_reason: changeReason,
    });
    return response.data;
  },

  getSupplierHistory: async (id: string): Promise<SupplierHistoryResponse> => {
    const response = await api.get(ApiEndpoints.suppliers.history(id));
    return response.data;
  },

  getSiteHistory: async (id: string): Promise<SiteHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.siteEndpoints.sites.history(id)
    );
    return response.data;
  },

  getLocationHistory: async (id: string): Promise<LocationHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.LocationrtkQuery.locations.history(id)
    );
    return response.data;
  },

  getAssetConditionHistory: async (id: string): Promise<AssetConditionHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.assetConditions.history(id)
    );
    return response.data;
  },

  getAssetStatusHistory: async (id: string): Promise<AssetStatusHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.assetStatuses.history(id)
    );
    return response.data;
  },

  getAssetCategoryHistory: async (id: string): Promise<AssetCategoryHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.assetCategories.history(id)
    );
    return response.data;
  },

  getManufacturerHistory: async (id: string): Promise<ManufacturerHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.manufacturers.history(id)
    );
    return response.data;
  },

  getModelHistory: async (id: string): Promise<ModelHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.models.history(id)
    );
    return response.data;
  },
  getDepartmentHistory: async (id: string): Promise<DepartmentHistoryResponse> => {
    const response = await api.get(
      ApiEndpoints.departments.history(id)
    );
    return response.data;
  },
};

export default assetService;

export const fetchUserDetails = async (
  dropdown?: boolean
): Promise<Users[]> => {
  try {
    const query = dropdown ? '?dropdown=true' : '';
    const response = await api.get(`users/${query}`);
    return response.data?.results || [];
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch assigned user'));
  }
};

// New dropdown-specific method for users
export const fetchUserDetailsDropdown = async (): Promise<Users[]> => {
  try {
    const response = await api.get('users/dropdown/');
    return response.data?.results || [];
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch assigned user'));
  }
};
